import React, { useState } from "react";
import { Box, Button, Dialog, Tooltip, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LoopIcon from "@mui/icons-material/Loop";
import DeleteIcon from "@mui/icons-material/Delete";
import styles from "./ActivityList.module.css";
import moment from "moment";
import "moment-timezone";
import stageDot from "../../assets/images/stage-dot.svg";
import nameLine from "../../assets/images/name-line.svg";
import highFlagIcon from "../FlagAssign/assets/High.svg";
import urgentFlagIcon from "../FlagAssign/assets/Urgent.svg";
import normalFlagIcon from "../FlagAssign/assets/Normal.svg";
import lowFlagIcon from "../FlagAssign/assets/Low.svg";
import TaskStatusPopup from "../TaskStatusCheckboxPopup/TaskStatusPopup";
import DeletePopup from "../DeletePopup/DeletePopup";
import { useAuthUser } from "react-auth-kit";
import {
  resetDeleteTaskSuccess,
  resetCommentStatuses,
  deleteTask,
} from "../../pages/app/Nurture/service/nurtureSlice";
import { useDispatch } from "react-redux";
import TaskEditPopup from "./TaskEditPopup/TaskEditPopup";
import checkCircle from "../../assets/images/check-circle.svg";
import checkCircleOutline from "../../assets/images/round-check.svg";
import commentIcon from "../../assets/images/commentIcon.svg";
import AddComment from "./AddComment/AddComment";
import CommentListItem from "./AddComment/CommentListItem";
import { useSelector } from "react-redux";
import getAccessStatus, {
  getSubDomainAccessStatus,
} from "../RolePermissionAccess/RolePermissionAccess";
import getTeamMember from "../CommonFunctions/CommonFunctions";
import { getDescriptionWithMentionedNames } from "../CommonFunctions/CommonFunctions";

const TaskListItem = React.memo(function TaskListItem(props: any) {
  const {
    data,
    DeleteTask,
    setFromTaskEdit,
    setTaskForEditing,
    setShowAddTask,
    showAddTask,
    UpdateTaskStatus,
    employees,
    activityList,
    selectedLeadData,
    updateTaskStatusSuccess,
    CreateComment,
    DeleteComment,
    EditComment,
    leadById,
    taskIdForComment,
    setTaskIdForComment,
    listView,
    fromSideDrawer,
    isGeneralTask,
    onEditClick,
  } = props;

  const auth = useAuthUser();
  const authUser = auth();
  const dispatch = useDispatch();
  const { userById } = useSelector((state: any) => state.userDetails);

  const hasLeadAccess = getSubDomainAccessStatus(
    "NURTURE_BOARD",
    "LEAD_CARD",
    "ALL_ACCESS",
    userById?.rolePermission,
  );
  const hasAllTaskAccess = getAccessStatus(
    "TASKS",
    "ALL_OTHER_USERS'_TASK",
    userById?.rolePermission,
  );
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [showDeleteChoiceModal, setShowDeleteChoiceModal] = useState(false);
  const [deleteChoice, setDeleteChoice] = useState<"series" | "only" | null>(
    null,
  );
  const [dataForDelete, setDataForDelete] = useState<any>();
  const {
    deleteTaskLoading,
    createCommentSuccess,
    updateCommentSuccess,
    lastCreatedComment,
  } = useSelector((state: any) => state.nurture);

  const handleDeleteButtonClick = () => {
    setDataForDelete(data);
    if (isGeneralTask) {
      if (data?.isRecurring === false) {
        setShowDeletePopup(true);
        setDeleteChoice(null);
        return;
      }

      const isRecurring = !!(
        data?.recurringTaskId ||
        data?.recurrence ||
        data?.recurringInfo ||
        data?.isRecurring
      );
      if (isRecurring) {
        setShowDeleteChoiceModal(true);
      } else {
        setShowDeletePopup(true);
      }
    } else {
      setShowDeletePopup(true);
    }
  };

  const handleDeleteSeriesChoice = () => {
    setDeleteChoice("series");
    setShowDeleteChoiceModal(false);
    setShowDeletePopup(true);
  };

  const handleDeleteOnlyThisChoice = () => {
    setDeleteChoice("only");
    setShowDeleteChoiceModal(false);
    setShowDeletePopup(true);
  };

  const handleCancelDeleteChoice = () => {
    setShowDeleteChoiceModal(false);
    setDeleteChoice(null);
  };
  const handleEditButtonClick = (val: any) => {
    if (onEditClick) {
      onEditClick(val);
      return;
    }
    setShowAddTask(!showAddTask);
    setTaskForEditing(val);
    setFromTaskEdit(true);
  };

  const handlePopupYesButton = () => {
    if (authUser && authUser?.institute?.[0]) {
      if (isGeneralTask) {
        const instituteId =
          authUser?.institute?.[0]?.instituteId ||
          authUser?.institute?.[0]?.instituteDetails?.id;
        const isRecurring = !!(
          dataForDelete?.recurringTaskId ||
          dataForDelete?.recurrence ||
          dataForDelete?.recurringInfo ||
          dataForDelete?.isRecurring
        );
        const actionToPerform =
          isRecurring && deleteChoice === "series"
            ? "DELETE_GENERAL_RECURRING_TASK"
            : "DELETE_GENERAL_TASK";
        const payload = {
          idsToDelete: [dataForDelete?.id],
          instituteId,
          updatedById: authUser?.institute?.[0]?.id,
          updatedByName: authUser?.firstName + " " + authUser?.lastName,
          actionToPerform,
          taskCategory: "GENERAL_TASK",
        };
        dispatch<any>(deleteTask(payload));
        setShowDeletePopup(false);
        setDeleteChoice(null);
      } else {
        const payload = {
          idsToDelete: [dataForDelete?.id],
          instituteId: authUser?.institute?.[0]?.instituteId,
          enquiryId: selectedLeadData?.id,
          studentName:
            selectedLeadData?.studentFirstName +
            selectedLeadData?.studentLastName,
          studentEmail: selectedLeadData?.studentEmail,
          swimlaneState: selectedLeadData?.enquirySwimlaneStatus,
          updatedById: authUser?.institute?.[0]?.id,
          updatedByName: authUser?.firstName + " " + authUser?.lastName,
          taskData: dataForDelete,
          actionToPerform: "DELETE_TASK",
        };
        DeleteTask(payload);
        setShowDeletePopup(false);
        setDeleteChoice(null);
        setTimeout(() => {
          dispatch(resetDeleteTaskSuccess());
        }, 3000);
      }
    } else {
      setShowDeletePopup(false);
      setDeleteChoice(null);
    }
  };

  const handlePopupNoButton = () => {
    setShowDeletePopup(false);
    setDeleteChoice(null);
  };

  const modifiedDescription = getDescriptionWithMentionedNames(
    activityList ? data?.taskData?.description : data?.description,
    employees,
  ).replace(/\n/g, "<br>");

  const creatorId = authUser?.institute?.[0]?.id;

  const [taskId, setTaskId] = useState<string>("");
  const [commentIdFromUrl, setCommentIdFromUrl] = useState<string>("");

  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const leadTaskId = urlParams.get("leadTaskId");
    if (leadTaskId) {
      setTaskId(leadTaskId);
    }
    const leadCommentId = urlParams.get("leadTaskCommentId");
    if (leadCommentId) {
      setCommentIdFromUrl(leadCommentId);
    }
    const timeoutId = setTimeout(() => {
      setTaskId("");
      setCommentIdFromUrl("");
      urlParams.delete("leadTaskId");
      urlParams.delete("leadTaskCommentId");

      const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
      window.history.pushState({}, "", newUrl);
    }, 5000); // 15 seconds

    return () => {
      clearTimeout(timeoutId);
    };
  }, []);

  const [showAddComment, setShowAddComment] = useState(false);
  const [fromCommentEdit, setFromCommentEdit] = useState<boolean>(false);
  const [CommentForEditing, setCommentForEditing] = useState<any>();

  const handleAddComment = () => {
    setShowAddComment(!showAddComment);
    setFromCommentEdit(false);
    setTaskIdForComment(data?.id);
  };

  const handleCancelComment = () => {
    setShowAddComment(false);
    setFromCommentEdit(false);
    setTaskIdForComment("");
  };

  React.useEffect(() => {
    if (taskIdForComment) {
      const dataIdStr = data?.id ? String(data.id) : null;
      const taskIdForCommentStr = taskIdForComment
        ? String(taskIdForComment)
        : null;
      if (dataIdStr !== taskIdForCommentStr) {
        setShowAddComment(false);
      }
    }
  }, [taskIdForComment, data?.id]);

  React.useEffect(() => {
    if (
      (createCommentSuccess !== false && createCommentSuccess !== true) ||
      (updateCommentSuccess !== false && updateCommentSuccess !== true)
    ) {
      const dataIdStr = data?.id ? String(data.id) : null;
      const taskIdForCommentStr = taskIdForComment
        ? String(taskIdForComment)
        : null;
      if (dataIdStr === taskIdForCommentStr) {
        setShowAddComment(false);
        setFromCommentEdit(false);
        setCommentForEditing(undefined);
      }
      setTimeout(() => {
        dispatch(resetCommentStatuses());
      }, 500);
    }
  }, [
    createCommentSuccess,
    updateCommentSuccess,
    taskIdForComment,
    data?.id,
    dispatch,
  ]);

  const instituteData = JSON.parse(localStorage.getItem("ACTIVE_INSTITUTE")!);

  return (
    <>
      <Box
        sx={{
          width: "100%",
          margin: "0 15px 22px 15px",
          background:
            taskId === data?.id && commentIdFromUrl === ""
              ? "#e8faf6"
              : "transparent",
          padding:
            taskId === data?.id && commentIdFromUrl === ""
              ? "0 5px 5px 5px"
              : "0px",
          borderRadius:
            taskId === data?.id && commentIdFromUrl === "" ? "4px" : "0px",
          transition: "0.1s ease-in-out",
        }}
      >
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            className={styles.activityHead}
            style={
              fromSideDrawer || isGeneralTask
                ? {
                    marginTop:
                      data?.status === "COMPLETED" && isGeneralTask
                        ? "9px"
                        : "16px",
                  }
                : {}
            }
          >
            <div className={styles.activityTop}>
              <div className={styles.activityHeadTaskStatus}>
                {(creatorId === data?.assignedToId || hasAllTaskAccess) &&
                  data?.taskType !== "DEMO" && (
                    <div className={styles.activityHeadTaskStatusCheckbox}>
                      <TaskStatusPopup
                        data={data}
                        UpdateTaskStatus={UpdateTaskStatus}
                        updateTaskStatusSuccess={updateTaskStatusSuccess}
                      />
                    </div>
                  )}
                {(data?.taskType === "DEMO" ||
                  (creatorId !== data?.assignedToId && !hasAllTaskAccess)) && (
                  <div
                    className={styles.activityHeadTaskStatusValue}
                    style={{
                      color:
                        data?.status === "TO_DO"
                          ? "#667085"
                          : data?.status === "IN_PROGRESS"
                            ? "#2664DC"
                            : data?.status === "BLOCKED"
                              ? "#EC3939"
                              : data?.status === "COMPLETED"
                                ? "#23A67B"
                                : "#2664DC",
                      background:
                        data?.status === "TO_DO"
                          ? "rgba(102, 112, 133, 0.10)"
                          : data?.status === "IN_PROGRESS"
                            ? "rgba(38, 100, 220, 0.07)"
                            : data?.status === "BLOCKED"
                              ? "rgba(236, 57, 57, 0.07)"
                              : data?.status === "COMPLETED"
                                ? "rgba(35, 166, 123, 0.06)"
                                : "rgba(35, 166, 123, 0.06)",
                    }}
                  >
                    {/* <img
                      src={
                        data?.status !== "COMPLETED"
                          ? checkCircleOutline
                          : checkCircle
                      }
                      alt="check"
                      style={{ marginRight: "6px" }}
                    /> */}
                    {data?.status?.toLowerCase()?.replace(/_/g, " ")}
                  </div>
                )}
              </div>
              {!isGeneralTask && (
                <div className={styles.activityHeadStage}>
                  <div className={styles.activityHeadStageIcon}>
                    <img src={stageDot} alt="" />
                  </div>
                  <div>
                    {/* {data?.enquirySwimlaneStatus
                      .toLowerCase()
                      ?.replace(/_/g, " ")} */}
                    {data?.enquirySwimlaneStatus
                      ?.toLowerCase()
                      ?.replace(/_/g, " ")}
                  </div>
                </div>
              )}
              <div className={styles.activityHeadName}>
                <div className={styles.activityHeadStageIcon}>
                  <img src={nameLine} alt="" />
                </div>
                <div style={{ textTransform: "capitalize" }}>
                  {/* {data?.createdByName} */}
                  {getTeamMember(data?.createdById, employees)?.firstName ===
                  undefined
                    ? data?.createdByName
                    : getTeamMember(data?.createdById, employees)?.firstName +
                      " " +
                      getTeamMember(data?.createdById, employees)?.lastName}
                </div>
              </div>
            </div>
            {data?.taskType !== "DEMO" &&
              (fromSideDrawer === false || isGeneralTask === true) && (
                <>
                  {data?.status === "COMPLETED" && (
                    <div style={{ color: "#11C89B", fontSize: "12px" }}>
                      Completed On{" "}
                      {data?.taskCompletedDate &&
                        moment(data?.taskCompletedDate).format(
                          "DD MMM YYYY, hh:mm A",
                        )}
                    </div>
                  )}
                  {data?.status !== "COMPLETED" &&
                  (creatorId === data?.createdById ||
                    creatorId === data?.assignedToId ||
                    hasAllTaskAccess) ? (
                    <>
                      {showDeleteChoiceModal && (
                        <Dialog
                          open={showDeleteChoiceModal}
                          onClose={handleCancelDeleteChoice}
                          sx={{
                            zIndex: "999999",
                            "& .MuiDialog-paper": {
                              width: "100%",
                              maxWidth: "400px",
                              padding: "34px",
                              borderRadius: "8px",
                              boxShadow:
                                "0px 20px 24px -4px rgba(16, 24, 40, 0.08), 0px 8px 8px -4px rgba(16, 24, 40, 0.03)",
                              background: "#FFFFFF",
                              position: "relative",
                            },
                          }}
                        >
                          <IconButton
                            onClick={handleCancelDeleteChoice}
                            sx={{
                              position: "absolute",
                              top: "22px",
                              right: "12px",
                              color: "#667085",
                              "&:hover": {
                                backgroundColor: "rgba(0, 0, 0, 0.04)",
                              },
                            }}
                          >
                            <CloseIcon />
                          </IconButton>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "row",
                              alignItems: "flex-start",
                              marginBottom: "24px",
                              gap: "12px",
                            }}
                          >
                            <DeleteIcon
                              sx={{
                                fontSize: "24px",
                                color: "#EC3939",
                                marginTop: "2px",
                                flexShrink: 0,
                              }}
                            />
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                flex: 1,
                              }}
                            >
                              <div
                                style={{
                                  fontSize: "16px",
                                  fontWeight: 600,
                                  color: "#101828",
                                  marginBottom: "8px",
                                }}
                              >
                                Delete Task
                              </div>
                              <div
                                style={{
                                  fontSize: "13px",
                                  fontWeight: 500,
                                  color: "#101828",
                                }}
                              >
                                Would you like to delete this task or the entire
                                series?
                              </div>
                            </Box>
                          </Box>
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "row",
                              gap: "12px",
                              justifyContent: "space-between",
                            }}
                          >
                            <Button
                              sx={{
                                textTransform: "capitalize",
                                backgroundColor: "#EC3939",
                                color: "#ffffff",
                                "&:hover": { backgroundColor: "#d63232" },
                                flex: 1,
                              }}
                              onClick={handleDeleteOnlyThisChoice}
                            >
                              Delete this task
                            </Button>
                            {!!(
                              dataForDelete?.recurringTaskId ||
                              dataForDelete?.recurrence ||
                              dataForDelete?.recurringInfo ||
                              dataForDelete?.isRecurring
                            ) && (
                              <Button
                                sx={{
                                  textTransform: "capitalize",
                                  backgroundColor: "#EC3939",
                                  color: "#ffffff",
                                  "&:hover": { backgroundColor: "#d63232" },
                                  flex: 1,
                                }}
                                onClick={handleDeleteSeriesChoice}
                              >
                                Delete entire series
                              </Button>
                            )}
                          </div>
                        </Dialog>
                      )}
                      {(instituteData?.migrationStarted === false ||
                        instituteData?.isMigrated === false) && (
                        <>
                          {showDeletePopup && (
                            <DeletePopup
                              type={"task"}
                              handlePopupYesButton={handlePopupYesButton}
                              handlePopupNoButton={handlePopupNoButton}
                              loading={deleteTaskLoading}
                              customMessage={
                                isGeneralTask && deleteChoice === "series"
                                  ? "Are you sure you want to delete the entire series?"
                                  : isGeneralTask && deleteChoice === "only"
                                    ? "Are you sure you want to delete this task?"
                                    : undefined
                              }
                            />
                          )}
                          <TaskEditPopup
                            handleDeleteButtonClick={handleDeleteButtonClick}
                            handleEditButtonClick={handleEditButtonClick}
                            data={data}
                          />
                        </>
                      )}
                    </>
                  ) : (
                    <></>
                  )}
                </>
              )}
          </div>
          {/* <div className={styles.activityDescription}>{data?.description}</div> */}
          <div
            className={styles.activityDescription}
            style={
              fromSideDrawer || isGeneralTask
                ? {
                    marginTop:
                      data?.status === "COMPLETED" && isGeneralTask
                        ? "9px"
                        : "16px",
                  }
                : {}
            }
            dangerouslySetInnerHTML={{ __html: modifiedDescription }}
          />

          <div
            className={styles.activityBottom}
            style={
              fromSideDrawer || isGeneralTask
                ? {
                    marginTop:
                      data?.status === "COMPLETED" && isGeneralTask
                        ? "9px"
                        : "16px",
                  }
                : {}
            }
          >
            <div className={styles.activityBottomLeft}>
              {data && data.assignedToName && (
                <div className={styles.activityBottomAssign}>
                  {getTeamMember(data?.assignedToId, employees)
                    ?.firstName?.charAt(0)
                    ?.toUpperCase()}
                  {getTeamMember(data?.assignedToId, employees)
                    ?.lastName?.charAt(0)
                    ?.toUpperCase()}
                </div>
              )}
              {data?.taskType !== "DEMO" && (
                <div className={styles.activityBottomFlag}>
                  <div className={styles.activityBottomFlagImg}>
                    <img
                      src={
                        data?.priority === "URGENT"
                          ? urgentFlagIcon
                          : data?.priority === "HIGH"
                            ? highFlagIcon
                            : data?.priority === "NORMAL"
                              ? normalFlagIcon
                              : data?.priority === "LOW"
                                ? lowFlagIcon
                                : lowFlagIcon
                      }
                      alt=""
                    />
                  </div>
                </div>
              )}
              {isGeneralTask &&
                fromSideDrawer &&
                data?.isRecurring === true && (
                  <div
                    className={styles.activityBottomFlag}
                    style={{ marginLeft: "2px" }}
                  >
                    <Tooltip title="Recurrence Task" arrow>
                      <LoopIcon
                        sx={{
                          fontSize: "14px",
                          color: "#11C89B",
                        }}
                      />
                    </Tooltip>
                  </div>
                )}
              <div
                className={styles.activityDateStyle}
                // style={{
                //   color:
                //     data?.taskType !== "DEMO"
                //       ? moment(data.date) < moment()
                //         ? "red"
                //         : "inherit"
                //       : moment(data?.demoData[0]?.demoTiming).format("DD MMM") <
                //         moment()
                //       ? "red"
                //       : "inherit",
                // }}
                style={{
                  color:
                    data?.taskType !== "DEMO"
                      ? moment(data?.date).isBefore(moment()) // Use isBefore for date comparison
                        ? "red"
                        : "inherit"
                      : moment(data?.demoData?.[0]?.demoTiming).isBefore(
                            moment(),
                          ) &&
                          (data?.demoData?.[0]?.demoStartTime?.toString()
                            ?.length === 10
                            ? moment()
                                .startOf("day")
                                .add({
                                  hours: moment
                                    .unix(data?.demoData?.[0]?.demoStartTime)
                                    .hour(),
                                  minutes: moment
                                    .unix(data?.demoData?.[0]?.demoStartTime)
                                    .minute(),
                                })
                                .add(30, "minutes")
                                .isBefore(moment())
                            : moment()
                                .startOf("day")
                                .add({
                                  hours: moment(
                                    data?.demoData?.[0]?.demoStartTime,
                                  ).hour(),
                                  minutes: moment(
                                    data?.demoData?.[0]?.demoStartTime,
                                  ).minute(),
                                })
                                .add(30, "minutes")
                                .isBefore(moment()))
                        ? "red"
                        : "inherit",
                  //  : moment(data?.demoData[0]?.demoTiming).isBefore(moment())
                  // ? "red"
                  // : "inherit",
                }}
              >
                Due on{" "}
                {data?.taskType !== "DEMO"
                  ? isGeneralTask ||
                    data?.taskCategory?.toUpperCase() === "GENERAL_TASK"
                    ? moment(data?.date)
                        .tz("Asia/Kolkata")
                        .format("DD MMM YYYY, hh:mm A")
                    : moment(data?.date).format("DD MMM YYYY, hh:mm A")
                  : moment(data?.demoData[0]?.demoTiming).format("DD MMM") +
                    " " +
                    (data?.demoData[0]?.demoStartTime?.toString()?.length === 10
                      ? moment
                          .unix(data?.demoData[0]?.demoStartTime)
                          .add(30, "minutes")
                          .format("hh:mm A")
                      : moment(data?.demoData[0]?.demoStartTime)
                          .add(30, "minutes")
                          .format("hh:mm A"))}
              </div>
            </div>
            {!listView && (
              <div>
                {!showAddComment && (
                  <Tooltip
                    arrow
                    title={
                      hasLeadAccess === false
                        ? "Oops! No permission."
                        : "Add comment"
                    }
                    // title={"Add comment"}
                    PopperProps={{ style: { zIndex: 999999 } }}
                    componentsProps={{
                      tooltip: {
                        sx: {
                          bgcolor: "#101828",
                          color: "#fff",
                          fontSize: "12px",
                          fontWeight: "500",
                          padding: "8px 12px",
                          "& .MuiTooltip-arrow": {
                            color: "#101828",
                          },
                        },
                      },
                    }}
                  >
                    <span
                      style={{
                        width: "100%",
                        display: "block",
                        textAlign: "center",
                      }}
                    >
                      <button
                        onClick={handleAddComment}
                        className={styles.commentBtn}
                        disabled={hasLeadAccess === false ? true : false}
                      >
                        <img src={commentIcon} alt="commentIcon" />
                      </button>
                    </span>
                  </Tooltip>
                )}
              </div>
            )}
          </div>

          {!listView && (
            <div
              style={{
                marginTop: isGeneralTask && fromSideDrawer ? "24px" : "0px",
              }}
            >
              {showAddComment &&
                taskIdForComment &&
                data?.id &&
                String(data.id) === String(taskIdForComment) && (
                  <AddComment
                    handleAddComment={handleAddComment}
                    employees={employees}
                    CreateComment={CreateComment}
                    EditComment={EditComment}
                    leadById={leadById}
                    taskId={data?.id}
                    fromCommentEdit={fromCommentEdit}
                    CommentForEditing={CommentForEditing}
                    handleCancelComment={handleCancelComment}
                    isGeneralTask={isGeneralTask}
                  />
                )}
              {!showAddComment && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    paddingTop: "8px",
                    paddingBottom: "8px",
                    gap: isGeneralTask && fromSideDrawer ? "16px" : "8px",
                  }}
                >
                  {(() => {
                    let commentsToShow: any[] = [];

                    if (
                      data?.commentList &&
                      Array.isArray(data.commentList) &&
                      data.commentList.length > 0
                    ) {
                      commentsToShow = [...data.commentList];
                    }

                    if (
                      lastCreatedComment &&
                      lastCreatedComment.taskId &&
                      String(lastCreatedComment.taskId) === String(data?.id)
                    ) {
                      const commentExists = commentsToShow.find(
                        (c: any) =>
                          String(c.id) === String(lastCreatedComment.id),
                      );

                      if (!commentExists) {
                        commentsToShow = [
                          ...commentsToShow,
                          lastCreatedComment,
                        ];
                      }
                    } else if (lastCreatedComment) {
                    }
                    return commentsToShow;
                  })().map((item: any, index: any) => (
                    <CommentListItem
                      key={item?.id || index}
                      employees={employees}
                      DeleteComment={DeleteComment}
                      commentData={item}
                      setShowAddComment={setShowAddComment}
                      setFromCommentEdit={setFromCommentEdit}
                      setCommentForEditing={setCommentForEditing}
                      leadById={leadById}
                      setTaskIdForComment={setTaskIdForComment}
                      taskId={data?.id}
                      commentIdFromUrl={commentIdFromUrl}
                      isGeneralTask={isGeneralTask}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </Box>
      </Box>
    </>
  );
});

export default TaskListItem;
